import { __ } from "@wordpress/i18n"
import { 
  useBlockProps, 
  InspectorControls,
  RichText,
  MediaUpload,
  MediaUploadCheck
} from "@wordpress/block-editor"
import { 
  PanelBody,
  Button,
  SelectControl,
  ToggleControl
} from "@wordpress/components"
import ServerSideRender from "@wordpress/server-side-render"
import { useState } from "@wordpress/element"

export default function Edit({ attributes, setAttributes, clientId }) {
  const blockProps = useBlockProps({
    className: `custom-banner ${attributes.align ? `align${attributes.align}` : ''}`
  })

  const [isEditing, setIsEditing] = useState(false)

  // If we're editing, show the rich text editors instead of ServerSideRender
  if (isEditing) {
    return (
      <>
        <InspectorControls>
          <PanelBody title={__('Custom Banner Settings', 'puroclean-tw')} initialOpen={true}>
            {/* Banner Image */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                {__('Banner Image:', 'puroclean-tw')}
              </label>
              <MediaUploadCheck>
                <MediaUpload
                  onSelect={(media) => setAttributes({ 
                    bannerImage: {
                      url: media.url,
                      id: media.id,
                      alt: media.alt || 'Banner Image'
                    }
                  })}
                  allowedTypes={['image']}
                  value={attributes.bannerImage.id}
                  render={({ open }) => (
                    <div>
                      {attributes.bannerImage.url ? (
                        <div>
                          <img 
                            src={attributes.bannerImage.url} 
                            alt={attributes.bannerImage.alt}
                            style={{ maxWidth: '200px', marginBottom: '10px', display: 'block' }}
                          />
                          <Button onClick={open} variant="secondary" style={{ marginRight: '10px' }}>
                            {__('Change Image', 'puroclean-tw')}
                          </Button>
                          <Button 
                            onClick={() => setAttributes({ 
                              bannerImage: { url: '', id: 0, alt: 'Banner Image' }
                            })} 
                            variant="link" 
                            isDestructive
                          >
                            {__('Remove Image', 'puroclean-tw')}
                          </Button>
                        </div>
                      ) : (
                        <Button onClick={open} variant="secondary">
                          {__('Select Banner Image', 'puroclean-tw')}
                        </Button>
                      )}
                    </div>
                  )}
                />
              </MediaUploadCheck>
            </div>

            {/* Heading Level */}
            <SelectControl
              label={__('Heading Level', 'puroclean-tw')}
              value={attributes.headingLevel}
              options={[
                { label: 'H2', value: 'h2' },
                { label: 'H3', value: 'h3' },
                { label: 'H4', value: 'h4' },
                { label: 'H5', value: 'h5' }
              ]}
              onChange={(value) => setAttributes({ headingLevel: value })}
              style={{ marginBottom: '20px' }}
            />

            {/* Background Color */}
            <SelectControl
              label={__('Background Color', 'puroclean-tw')}
              value={attributes.backgroundColor}
              options={[
                { label: 'White', value: 'white' },
                { label: 'Gray', value: 'gray' },
                { label: 'Blue', value: 'blue' },
                { label: 'Primary', value: 'primary' },
                { label: 'Secondary', value: 'secondary' }
              ]}
              onChange={(value) => setAttributes({ backgroundColor: value })}
              style={{ marginBottom: '20px' }}
            />

            {/* Image Position */}
            <SelectControl
              label={__('Image Position', 'puroclean-tw')}
              value={attributes.imagePosition}
              options={[
                { label: 'Left', value: 'left' },
                { label: 'Right', value: 'right' }
              ]}
              onChange={(value) => setAttributes({ imagePosition: value })}
              style={{ marginBottom: '20px' }}
            />

            {/* Content Alignment */}
            <SelectControl
              label={__('Content Alignment', 'puroclean-tw')}
              value={attributes.contentAlignment}
              options={[
                { label: 'Left', value: 'left' },
                { label: 'Center', value: 'center' }
              ]}
              onChange={(value) => setAttributes({ contentAlignment: value })}
              style={{ marginBottom: '20px' }}
            />
          </PanelBody>
        </InspectorControls>

        <div {...blockProps}>
          <div style={{ 
            border: '2px dashed #ddd', 
            padding: '40px 20px', 
            textAlign: 'center',
            backgroundColor: '#f9f9f9'
          }}>
            <h3 style={{ marginBottom: '20px', color: '#333' }}>
              {__('Custom Banner - Edit Mode', 'puroclean-tw')}
            </h3>
            
            <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'left' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                  {__('Heading:', 'puroclean-tw')} ({attributes.headingLevel.toUpperCase()})
                </label>
                <RichText
                  tagName="div"
                  placeholder={__('Enter heading text...', 'puroclean-tw')}
                  value={attributes.heading}
                  onChange={(value) => setAttributes({ heading: value })}
                  style={{ 
                    border: '1px solid #ddd', 
                    padding: '12px', 
                    minHeight: '50px',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    backgroundColor: 'white'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '30px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                  {__('Content:', 'puroclean-tw')}
                </label>
                <RichText
                  tagName="div"
                  placeholder={__('Add your content here. You can include paragraphs and format text...', 'puroclean-tw')}
                  value={attributes.content}
                  onChange={(value) => setAttributes({ content: value })}
                  style={{ 
                    border: '1px solid #ddd', 
                    padding: '12px', 
                    minHeight: '120px',
                    fontSize: '16px',
                    backgroundColor: 'white'
                  }}
                />
              </div>
              
              <div style={{ textAlign: 'center' }}>
                <Button 
                  variant="primary"
                  onClick={() => setIsEditing(false)}
                  style={{ fontSize: '16px', padding: '10px 20px' }}
                >
                  {__('Done Editing - View Preview', 'puroclean-tw')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  // Show preview mode with ServerSideRender
  return (
    <>
      <InspectorControls>
        <PanelBody title={__('Custom Banner Settings', 'puroclean-tw')} initialOpen={true}>
          <Button 
            variant="secondary"
            onClick={() => setIsEditing(true)}
            style={{ marginBottom: '15px', width: '100%' }}
          >
            {__('Edit Content', 'puroclean-tw')}
          </Button>
          
          {/* Banner Image */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
              {__('Banner Image:', 'puroclean-tw')}
            </label>
            <MediaUploadCheck>
              <MediaUpload
                onSelect={(media) => setAttributes({ 
                  bannerImage: {
                    url: media.url,
                    id: media.id,
                    alt: media.alt || 'Banner Image'
                  }
                })}
                allowedTypes={['image']}
                value={attributes.bannerImage.id}
                render={({ open }) => (
                  <div>
                    {attributes.bannerImage.url ? (
                      <div>
                        <img 
                          src={attributes.bannerImage.url} 
                          alt={attributes.bannerImage.alt}
                          style={{ maxWidth: '200px', marginBottom: '10px', display: 'block' }}
                        />
                        <Button onClick={open} variant="secondary" style={{ marginRight: '10px' }}>
                          {__('Change Image', 'puroclean-tw')}
                        </Button>
                        <Button 
                          onClick={() => setAttributes({ 
                            bannerImage: { url: '', id: 0, alt: 'Banner Image' }
                          })} 
                          variant="link" 
                          isDestructive
                        >
                          {__('Remove Image', 'puroclean-tw')}
                        </Button>
                      </div>
                    ) : (
                      <Button onClick={open} variant="secondary">
                        {__('Select Banner Image', 'puroclean-tw')}
                      </Button>
                    )}
                  </div>
                )}
              />
            </MediaUploadCheck>
          </div>

          {/* Heading Level */}
          <SelectControl
            label={__('Heading Level', 'puroclean-tw')}
            value={attributes.headingLevel}
            options={[
              { label: 'H2', value: 'h2' },
              { label: 'H3', value: 'h3' },
              { label: 'H4', value: 'h4' },
              { label: 'H5', value: 'h5' }
            ]}
            onChange={(value) => setAttributes({ headingLevel: value })}
            style={{ marginBottom: '20px' }}
          />

          {/* Background Color */}
          <SelectControl
            label={__('Background Color', 'puroclean-tw')}
            value={attributes.backgroundColor}
            options={[
              { label: 'White', value: 'white' },
              { label: 'Gray', value: 'gray' },
              { label: 'Blue', value: 'blue' },
              { label: 'Primary', value: 'primary' },
              { label: 'Secondary', value: 'secondary' }
            ]}
            onChange={(value) => setAttributes({ backgroundColor: value })}
            style={{ marginBottom: '20px' }}
          />

          {/* Image Position */}
          <SelectControl
            label={__('Image Position', 'puroclean-tw')}
            value={attributes.imagePosition}
            options={[
              { label: 'Left', value: 'left' },
              { label: 'Right', value: 'right' }
            ]}
            onChange={(value) => setAttributes({ imagePosition: value })}
            style={{ marginBottom: '20px' }}
          />

          {/* Content Alignment */}
          <SelectControl
            label={__('Content Alignment', 'puroclean-tw')}
            value={attributes.contentAlignment}
            options={[
              { label: 'Left', value: 'left' },
              { label: 'Center', value: 'center' }
            ]}
            onChange={(value) => setAttributes({ contentAlignment: value })}
            style={{ marginBottom: '20px' }}
          />
        </PanelBody>
      </InspectorControls>

      <div {...blockProps}>
        <ServerSideRender
          block="puroclean/custom-banner"
          attributes={attributes}
          urlQueryArgs={{ context: 'edit' }}
        />
      </div>
    </>
  )
} 